import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastError from '../../errors/toastError';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
    },
  },
  dialogTitle: {
    background: theme.palette.background.default,
    paddingBottom: theme.spacing(1),
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  buttonWrapper: {
    position: 'relative',
    width: 'fit-content',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  cancelButton: {
    marginRight: theme.spacing(2),
  },
}));

const WebhookSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Nome muito curto!')
    .max(50, 'Nome muito longo!')
    .required('O nome é obrigatório'),
});

const WebhookModal = ({ open, onClose, webhookId, nameWebhook, onSave }) => {
  const classes = useStyles();

  const handleSaveWebhook = async (values, actions) => {
    try {
      if (webhookId) {
        await api.put('/webhook/updateWebHook', {
          name: values.name,
          webhookId,
        });
        toast.success(i18n.t('webhookModal.toasts.update'));
      } else {
        await api.post('/webhook/createWebHook', {
          name: values.name,
        });
        toast.success(i18n.t('webhookModal.saveSuccess'));
      }
      actions.setSubmitting(false);
      onSave(values.name);
      onClose();
    } catch (err) {
      toastError(err);
      actions.setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className={classes.root}
    >
      <Formik
        initialValues={{ name: nameWebhook || '' }}
        enableReinitialize={true}
        validationSchema={WebhookSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveWebhook(values, actions);
          }, 400);
        }}
      >
        {({ values, errors, touched, isSubmitting, handleChange }) => (
          <Form>
            <DialogTitle className={classes.dialogTitle}>
              {webhookId
                ? i18n.t('webhookModal.title.edit')
                : i18n.t('webhookModal.title.add')}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <TextField
                label={i18n.t('webhookModal.form.name')}
                name="name"
                value={values.name}
                onChange={handleChange}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                variant="outlined"
                margin="dense"
                fullWidth
                className={classes.textField}
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={onClose}
                color="secondary"
                variant="outlined"
                disabled={isSubmitting}
                className={classes.cancelButton}
              >
                {i18n.t('webhookModal.buttons.cancel')}
              </Button>
              <div className={classes.buttonWrapper}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {webhookId
                    ? i18n.t('webhookModal.buttons.okEdit')
                    : i18n.t('webhookModal.buttons.okAdd')}
                </Button>
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default WebhookModal;